import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, unwrapArray } from "@/lib/api";
import { LeadMergeDialog } from "./LeadMergeDialog";

interface Member {
  id: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  lead_status: string;
  owner?: string | null;
}
interface Group {
  reasons: string[];
  leads: Member[];
}
const label = (m: Member) => [m.first_name, m.last_name].filter(Boolean).join(" ") || m.company_name || "Lead";

/** The cleanup list: every group of leads you can see that share an email or phone number, oldest first, with a merge for each extra lead */
export function DuplicateGroupsDialog({ open, onClose, canMerge, onChanged }: { open: boolean; onClose: () => void; canMerge: boolean; onChanged: () => void }) {
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [pair, setPair] = useState<[string, string] | null>(null);

  const load = () =>
    api
      .get("/api/Lead/DuplicateGroups")
      .then((r) => setGroups(unwrapArray<Group>(r)))
      .catch(() => setGroups([]));

  useEffect(() => {
    if (open) {
      setGroups(null);
      void load();
    }
  }, [open]);

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Possible duplicate leads</DialogTitle>
            <DialogDescription>Leads that share an email or phone number. The oldest lead in each group is the one to keep; check each pair before merging.</DialogDescription>
          </DialogHeader>
          {groups === null && <p className="py-6 text-center text-sm text-muted-foreground">Looking…</p>}
          {groups?.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No duplicates found.</p>}
          <div className="space-y-3">
            {groups?.map((g, i) => (
              <section key={i} className="rounded-lg border border-border">
                <h3 className="border-b border-border bg-panel px-3 py-1.5 text-xs font-semibold text-muted-foreground">{g.reasons.join(" · ") || "Shared contact details"}</h3>
                {g.leads.map((m, j) => (
                  <div key={m.id} className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2 last:border-b-0">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">
                        {label(m)} {j === 0 && <span className="ml-1 text-[10.5px] font-normal text-primary">oldest</span>}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">{[m.company_name, m.email, m.phone, m.lead_status.toLowerCase(), m.owner].filter(Boolean).join(" · ")}</div>
                    </div>
                    {canMerge && j > 0 && (
                      <Button size="sm" variant="outline" onClick={() => setPair([g.leads[0]!.id, m.id])}>
                        Merge into oldest
                      </Button>
                    )}
                  </div>
                ))}
              </section>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <LeadMergeDialog
        open={!!pair}
        ids={pair}
        onClose={() => setPair(null)}
        onMerged={() => {
          void load();
          onChanged();
        }}
      />
    </>
  );
}
